import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Briefcase, GraduationCap, Award } from 'lucide-react';
import { workExperience } from '../mock';

const About = () => {
  const portraitUrl = '/images/portrait-hero.jpg';
  const resumeUrl = 'https://drive.google.com/uc?export=download&id=1tzIfB1Dk5UoNDRtcQMIl1oVuRGJETbFS';

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="pt-32 pb-12 px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img
              src={portraitUrl}
              alt="Pratish Jain"
              className="w-48 h-48 rounded-2xl object-cover shadow-xl border-4 border-amber-100"
            />
            <div>
              <Badge className="bg-amber-100 text-amber-700 mb-4">About</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">Pratish Jain</h1>
              <p className="text-xl text-gray-600">
                B2B SaaS Content Marketer | SEO Strategist | Lead Generation Specialist
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">My Story</h2>
          <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
            <p>
              I started my career as a <strong>content writer</strong>, crafting blogs and articles for a variety of brands. But I quickly realized something: great writing alone doesn't move the needle. It's what that writing <em>does</em> that matters.
            </p>
            <p>
              That realization led me to expand beyond writing into <strong>growth marketing</strong> and <strong>analytics</strong>. I learned how to use tools like Google Analytics, Search Console, and Clarity to understand user behavior. I studied SEO not just as a checkbox activity, but as a strategic lever for driving qualified traffic.
            </p>
            <p>
              Working with both D2C and B2B brands taught me an important lesson: <strong className="text-amber-600">traffic alone doesn't pay the bills</strong>. What matters is whether that traffic converts into leads, and whether those leads turn into revenue.
            </p>
            <p>
              This led me to specialize in <strong>B2B SaaS organic growth</strong>—where content strategy, SEO, and conversion optimization come together to create predictable pipeline growth.
            </p>
            <p>
              At companies like <strong>Engati</strong> and <strong>helo.ai</strong>, I didn't just write content. I built content engines—complete with topic clusters, lead magnets, conversion funnels, and analytics dashboards. The results speak for themselves: millions of impressions, thousands of monthly visitors, and hundreds of qualified leads.
            </p>
            <p>
              Today, I help B2B SaaS companies turn their content into a <strong>lead generation machine</strong>. Not just traffic. Not just vanity metrics. But real, measurable pipeline growth.
            </p>
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="py-16 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10">Qualifications</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="text-amber-600" size={20} />
                  </div>
                  <CardTitle>Experience</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">3+ years in content marketing and B2B SaaS growth</p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Award className="text-amber-600" size={20} />
                  </div>
                  <CardTitle>Specialization</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">B2B SaaS SEO, Lead Generation, Content Strategy, Analytics</p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="text-amber-600" size={20} />
                  </div>
                  <CardTitle>Education</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-700">
                  <li>• Masters in eCommerce (Mumbai University)</li>
                  <li>• Bachelors in Commerce (Mumbai University)</li>
                  <li>• Content Writing & Copywriting Pro (MyCaptain)</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Award className="text-amber-600" size={20} />
                  </div>
                  <CardTitle>Core Tools</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Google Analytics, Search Console, Clarity, Ahrefs, SEMrush, HubSpot</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Work Experience Timeline */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10">Work Experience</h2>
          <div className="space-y-8">
            {workExperience.map((job) => (
              <Card key={job.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-2xl">{job.company}</CardTitle>
                      <div className="text-amber-600 font-medium mt-1">{job.role}</div>
                    </div>
                    <Badge className="bg-gray-100 text-gray-700 w-fit">
                      {job.duration}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-700">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Download Resume CTA */}
      <section className="py-16 px-6 lg:px-8 bg-gradient-to-br from-amber-600 to-amber-700 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Want to know more?</h2>
          <p className="text-xl mb-8 opacity-90">
            Download my full resume to see detailed work experience, skills, and achievements.
          </p>
          <a
            href={resumeUrl}
            download="Pratish_Jain_Resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="bg-white text-amber-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all"
            >
              <FileText className="mr-2" size={20} />
              Download Resume
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;